/* lc-ga4-select-item.js
 *
 * Wires GA4 `select_item` event on product card clicks.
 *
 * Listens at document level for clicks anywhere inside an element
 * carrying `data-lc-product-card` (set on card-wrapper in
 * snippets/card-product.liquid). Pushes a `select_item` event to
 * window.dataLayer with the GA4 ecommerce schema, consumed by the
 * Google & YouTube app gtag — GA4's single sender (2026-07-17,
 * knowledge-graph/CLAUDE.md § LC tracking rules). The LC App Pixel is
 * Meta-only and there is no GTM container.
 *
 * Per CLAUDE.md correction 2026-05-14: GA4 records nullish parameter
 * values as `(not set)`. This module uses defensive fallbacks so no
 * field reaches GA4 as undefined / null.
 *
 * Event source flag `event_source: 'theme'` lets the downstream pixel
 * dedupe vs any future Customer-Events-side equivalent.
 *
 * AUD3-001 — wired 2026-05-19.
 */
(function () {
  'use strict';

  var ATTR_CARD = 'data-lc-product-card';
  var LIST_ATTR = 'data-lc-list-name';
  var LIST_ID_ATTR = 'data-lc-list-id';

  function nearestCard(el) {
    while (el && el.nodeType === 1) {
      if (el.hasAttribute && el.hasAttribute(ATTR_CARD)) return el;
      el = el.parentNode;
    }
    return null;
  }

  function deriveListName(card) {
    var explicit = card.closest('[' + LIST_ATTR + ']');
    if (explicit) return explicit.getAttribute(LIST_ATTR) || 'unknown_list';
    var path = (window.location && window.location.pathname) || '';
    var m = path.match(/^\/collections\/([^/?#]+)/);
    if (m) return 'collection_' + m[1];
    if (path === '/' || path === '/index') return 'homepage';
    if (/^\/search/.test(path)) return 'search_results';
    if (/^\/products\//.test(path)) return 'pdp_related_or_recommended';
    if (/^\/blogs\//.test(path)) return 'journal';
    return 'unknown_list';
  }

  function deriveListId(card) {
    var explicit = card.closest('[' + LIST_ID_ATTR + ']');
    if (explicit) return explicit.getAttribute(LIST_ID_ATTR) || 'unknown_list_id';
    return deriveListName(card);
  }

  function num(value) {
    var n = parseFloat(value);
    return isFinite(n) ? n : 0;
  }

  function safeString(value, fallback) {
    if (value === null || value === undefined) return fallback;
    var s = String(value).trim();
    return s === '' ? fallback : s;
  }

  function pushSelectItem(card) {
    try {
      window.dataLayer = window.dataLayer || [];
      var productId = safeString(card.getAttribute('data-lc-product-id'), 'unknown_product_id');
      var variantId = safeString(card.getAttribute('data-lc-variant-id'), productId);
      var title = safeString(card.getAttribute('data-lc-product-title'), 'unknown_product_title');
      var price = num(card.getAttribute('data-lc-price'));
      var currency = safeString(card.getAttribute('data-lc-currency'), 'AUD');
      var listName = deriveListName(card);
      var listId = deriveListId(card);

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'select_item',
        event_source: 'theme',
        ecommerce: {
          currency: currency,
          item_list_id: listId,
          item_list_name: listName,
          items: [{
            item_id: variantId,
            item_name: title,
            item_brand: 'Lost Collective',
            item_list_id: listId,
            item_list_name: listName,
            price: price,
            quantity: 1
          }]
        }
      });
    } catch (_err) {
      // Tracking errors must never break navigation.
    }
  }

  document.addEventListener('click', function (ev) {
    var card = nearestCard(ev.target);
    if (!card) return;
    if (ev.button !== undefined && ev.button !== 0) return;
    if (ev.defaultPrevented) return;
    pushSelectItem(card);
  }, { passive: true });

  document.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Enter') return;
    // Native links/buttons synthesize a click on Enter, which the click
    // handler above already tracks - pushing here too double-fires.
    if (ev.target && ev.target.closest && ev.target.closest('a, button')) return;
    var card = nearestCard(ev.target);
    if (!card) return;
    pushSelectItem(card);
  });
}());
